/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import InstrumentConfig from './InstrumentConfig';
import FretRangeSelector from './FretRangeSelector';

interface MobileToolsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;

  // Instrument config
  strings: number;
  frets: number;
  tuning: string[];
  onConfigChange: (config: { strings: number; frets: number; tuning: string[] }) => void;

  // Fret range
  fretRange: { start: number; end: number };
  onFretRangeChange: (range: { start: number; end: number }) => void;

  // Display settings
  showNoteNames: boolean;
  onShowNoteNamesChange: (show: boolean) => void;

  // User state
  isFree: boolean;
}

/**
 * MobileToolsSheet - Bottom sheet for instrument and display settings
 *
 * Accessed via the settings gear icon in the MobileControlBar.
 * Contains less frequently used settings like instrument config and display options.
 */
const MobileToolsSheet: React.FC<MobileToolsSheetProps> = ({
  isOpen,
  onOpenChange,
  strings,
  frets,
  tuning,
  onConfigChange,
  fretRange,
  onFretRangeChange,
  showNoteNames,
  onShowNoteNamesChange,
  isFree
}) => {
  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[70vh] overflow-y-auto">
        {/* Header with close button */}
        <div className="flex items-center justify-between pb-4 border-b mb-4">
          <SheetTitle>Settings</SheetTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-6 pb-20">
          {/* Instrument Configuration */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Instrument</h4>
            <InstrumentConfig
              onConfigChange={onConfigChange}
              currentStrings={strings}
              currentFrets={frets}
              currentTuning={tuning}
              isFree={isFree}
            />
          </div>

          <Separator />

          {/* Fret Range */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Fret Range</h4>
            <FretRangeSelector
              fretRange={fretRange}
              totalFrets={frets}
              onRangeChange={onFretRangeChange}
              isMobile={true}
            />
          </div>

          <Separator />

          {/* Display Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Display</h4>

            <div className="flex items-center justify-between">
              <label className="text-sm">Show Note Names</label>
              <Switch
                checked={showNoteNames}
                onCheckedChange={onShowNoteNamesChange}
              />
            </div>
          </div>
        </div>

        {/* Sticky Done button at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-background border-t">
          <Button
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileToolsSheet;
